import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { apiError } from "../api";
import { useAuth } from "../auth";
import { useLang } from "../i18n";

const GoogleMark = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 1.98 6.93l3.66 2.84C6.54 7.04 8.99 5.04 12 5.04z"/>
        <path fill="#4285F4" d="M23.25 12.27c0-.9-.08-1.57-.26-2.26H12v4.28h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.87 3.63-8.58z"/>
        <path fill="#FBBC05" d="M5.66 14.22a7.2 7.2 0 0 1 0-4.45L2 6.93a11.25 11.25 0 0 0 0 10.13l3.66-2.84z"/>
        <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.57-2.77c-.95.66-2.23 1.12-3.89 1.12-3.01 0-5.46-2-6.36-4.66l-3.66 2.84c1.85 3.67 5.64 6.19 10.02 6.19z"/>
    </svg>
);

export default function Login() {
    const { user, login, loginWithGoogle, loading } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            nav(user.role === "staff" ? "/checkin" : user.role === "admin" ? "/admin" : "/", { replace: true });
        }
    }, [user, loading, nav]);

    if (loading || user) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(form.email, form.password);
            toast.success(`Welcome back, ${u.name || u.email}`);
            nav(u.role === "staff" ? "/checkin" : u.role === "admin" ? "/admin" : "/");
        } catch (err) {
            toast.error(apiError(err, "Login failed."));
            setBusy(false);
        }
    };

    return (
        <div data-testid="login-page" className="pt-36 pb-24 min-h-screen flex items-start justify-center">
            <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md mx-4 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/80 backdrop-blur p-8 sm:p-10"
            >
                <div className="text-center">
                    <span className="font-display font-black text-2xl tracking-[0.22em] text-goldgrad">LAKOU</span>
                    <p className="mt-3 text-sm text-lakou-grey">{t("login_sub")}</p>
                </div>

                <button
                    type="button"
                    onClick={loginWithGoogle}
                    data-testid="google-signin-button"
                    className="mt-8 w-full flex items-center justify-center gap-3 rounded-full border border-lakou-gold/30 bg-lakou-raise/70 hover:border-lakou-gold hover:bg-lakou-raise py-3.5 font-display font-bold text-sm text-lakou-cream transition-colors duration-300"
                >
                    <GoogleMark /> {t("login_google")}
                </button>

                <div className="my-6 flex items-center gap-4">
                    <span className="flex-1 h-px bg-lakou-gold/15" />
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{t("login_or")}</span>
                    <span className="flex-1 h-px bg-lakou-gold/15" />
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                    <input
                        type="email" required placeholder={t("form_email")} autoComplete="email"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="login-email"
                        className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors"
                    />
                    <input
                        type="password" required placeholder="Password" autoComplete="current-password"
                        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="login-password"
                        className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors"
                    />
                    <button
                        type="submit" disabled={busy}
                        data-testid="login-submit"
                        className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50"
                    >
                        {busy ? "…" : t("login_btn")}
                    </button>
                </form>
                <p className="mt-6 text-center text-[11px] text-lakou-sand">{t("login_note")}</p>
            </motion.div>
        </div>
    );
}
